import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon, Tag, Newspaper, Share2 } from "lucide-react";
import { api } from "../../lib/api";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";
const REF_HOST = "istreshandball.com";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
}

/**
 * Rewrite all <a href="https://istreshandball.com/...">  to internal /sport/actualites/<slug>
 * (via slug regex extraction). Strip external <a target="_blank"> attributes.
 * Also rewrite href="https://www.lnh.fr/..." links to keep their external targets but mark them safe.
 */
function rewriteContentHtml(html) {
  if (!html) return "";
  // Rewrite WordPress permalinks → internal route
  html = html.replace(/href="https?:\/\/(?:www\.)?istreshandball\.com\/([^"#]+?)\/?(#[^"]*)?"/g, (m, slug, anchor) => {
    const cleanSlug = slug.split("/").filter(Boolean).pop();
    if (!cleanSlug) return 'href="/sport"';
    return `href="/sport/actualites/${cleanSlug}${anchor || ""}" data-internal="1"`;
  });
  // Drop target="_blank" on any remaining links pointing back to the reference site
  html = html.replace(/<a([^>]*)href="([^"]*istreshandball\.com[^"]*)"([^>]*)>/g, '<a$1href="/sport/actualites"$3>');
  return html;
}

export default function SportArticleDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setItem(null);
    api.get(`/sport/news/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (!alive) return;
        setItem(r.data?.item || null);
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => alive && setLoading(false));
    // Related (same category)
    api.get("/sport/news?limit=6")
      .then((r) => alive && setRelated(r.data?.items || []))
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  // Same-category subset
  const sameCat = useMemo(() => {
    if (!item) return related.slice(0, 4);
    const slugs = (item.categories || []).map((c) => c.slug);
    const out = related.filter((r) => r.slug !== item.slug && (r.categories || []).some((c) => slugs.includes(c.slug)));
    return (out.length ? out : related.filter((r) => r.slug !== item.slug)).slice(0, 4);
  }, [item, related]);

  const safeContent = useMemo(() => rewriteContentHtml(item?.content_html || ""), [item]);

  const sharePage = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: item?.title || "IPHB", url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
      // eslint-disable-next-line no-alert
      alert("Lien copié dans le presse-papier !");
    }
  };

  if (loading) {
    return (
      <div className="min-h-svh pt-32 pb-24" style={{ background: BG_DARK }}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8 animate-pulse">
          <div className="h-8 w-32 rounded-full bg-white/10 mb-6"/>
          <div className="h-12 w-3/4 rounded bg-white/10 mb-3"/>
          <div className="h-12 w-1/2 rounded bg-white/10 mb-8"/>
          <div className="aspect-[16/9] rounded-3xl bg-white/10 mb-8"/>
          <div className="space-y-3">
            <div className="h-4 rounded bg-white/10"/>
            <div className="h-4 rounded bg-white/10 w-5/6"/>
            <div className="h-4 rounded bg-white/10 w-4/6"/>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="min-h-svh flex items-center justify-center pt-24" style={{ background: BG_DARK }}>
        <div className="text-center text-white max-w-md mx-auto px-4">
          <Newspaper size={48} className="mx-auto text-pink-300/60 mb-4"/>
          <h1 className="font-blackhawk italic uppercase text-3xl">Article introuvable</h1>
          <p className="text-white/60 mt-3 text-sm">L'article que vous cherchez n'existe pas ou a été déplacé.</p>
          <Link to="/sport/actualites" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
            <ArrowLeft size={14}/> Retour aux actualités
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sport-article-detail" style={{ background: BG_DARK }}>
      {/* HERO */}
      <section className="relative pt-28 pb-10 sm:pt-36 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {item.image && <img src={item.image} alt="" className="w-full h-full object-cover"/>}
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${BG_DARK}cc 0%, ${PRIMARY}aa 50%, ${BG_DARK}f5 100%)` }}/>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 lg:px-8 text-white">
          <Link to="/sport/actualites" className="inline-flex items-center gap-2 text-pink-300 hover:text-white text-xs font-black uppercase tracking-widest transition mb-6"
                data-testid="article-back-link">
            <ArrowLeft size={14}/> Retour aux actualités
          </Link>

          {/* Category tags */}
          {!!(item.categories || []).length && (
            <div className="flex flex-wrap gap-2 mb-4">
              {item.categories.map((c) => (
                <span key={c.slug} className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg" style={{ background: SECONDARY }}>
                  {c.label}
                </span>
              ))}
              {(item.tags || []).map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-white/90 bg-white/15 backdrop-blur border border-white/20">
                  {t}
                </span>
              ))}
            </div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="font-blackhawk italic uppercase leading-[0.95]"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.25rem)" }}
          >
            {item.title}
          </motion.h1>

          <div className="mt-5 flex flex-wrap items-center gap-3 text-white/85 text-sm">
            <span className="inline-flex items-center gap-2">
              <CalendarIcon size={13} className="text-pink-300"/> {fmtDate(item.date_iso)}
            </span>
            <span className="text-white/30">·</span>
            <span className="inline-flex items-center gap-2 text-pink-300"><Tag size={12}/> #IPHB</span>
            <span className="text-white/30">·</span>
            <button onClick={sharePage} className="inline-flex items-center gap-2 hover:text-pink-300 transition" data-testid="article-share-btn">
              <Share2 size={13}/> Partager
            </button>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <article className="relative bg-white">
        <div className="max-w-3xl mx-auto px-4 lg:px-0 py-14 sm:py-20">
          {item.image && (
            <figure className="-mx-4 sm:mx-0 mb-10 rounded-2xl overflow-hidden shadow-2xl">
              <img src={item.image} alt={item.title} className="w-full aspect-[16/9] object-cover"/>
            </figure>
          )}
          <div
            className="article-prose prose prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:italic prose-h2:mt-10 prose-h2:text-3xl prose-h3:text-xl prose-a:text-pink-600 prose-a:font-bold prose-img:rounded-xl prose-img:shadow-md prose-strong:text-slate-900"
            style={{ color: "#1f2937" }}
            data-testid="article-content"
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />
        </div>
      </article>

      {/* RELATED */}
      {sameCat.length > 0 && (
        <section className="relative py-14 sm:py-20" style={{ background: BG_DEEP }}>
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">À découvrir</div>
                <h2 className="text-white font-blackhawk italic uppercase text-2xl sm:text-4xl leading-tight mt-1">
                  Autres <span style={{ color: SECONDARY }}>actualités</span>
                </h2>
              </div>
              <Link to="/sport/actualites" className="hidden sm:inline-flex items-center gap-2 text-pink-300 hover:text-white text-xs font-black uppercase tracking-widest transition">
                Toutes les news <ArrowRight size={13}/>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {sameCat.map((r) => (
                <Link
                  key={r.id}
                  to={`/sport/actualites/${r.slug}`}
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#1f0a3f] border border-white/10 hover:border-pink-400/60 transition shadow-xl hover:shadow-pink-900/30"
                  data-testid={`related-${r.id}`}
                >
                  {r.image && <img src={r.image} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(22,7,50,0.95) 100%)" }}/>
                  <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 text-white">
                    <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest mb-1">
                      {fmtDate(r.date_iso)}
                    </div>
                    <h3 className="font-blackhawk italic uppercase text-base sm:text-lg leading-tight" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
